/**
 * Configurazione delle schede di listino.
 *
 * IL PROGETTO PREVEDE UNA SOLA SCHEDA
 * -----------------------------------
 * Esiste una sola scheda di listino, generica e senza dati: la categoria
 * `altro`, etichettata «Listino». Non è una riduzione temporanea legata alla
 * modalità demo: le schede merceologiche del prodotto originale
 * (piastrelle, bagno, rivestimenti parete, parquet, laminati, profili) sono
 * state rimosse dalla configurazione e non sono riattivabili da una variabile
 * d'ambiente.
 *
 * PERCHÉ PROPRIO QUESTA
 * ---------------------
 * È la configurazione più leggera fra quelle supportate, valutata su ciò che
 * la scheda comporta davvero a runtime e non sulla lunghezza dello script di
 * import:
 *
 *  - Campi richiesti: 5 colonne (codice, brand, nome, descrizione, prezzo),
 *    il gruppo `generic` di ListiniCategoryPage. Le schede ad area ne
 *    richiedevano 10, parquet 10 con un formatter dedicato, i profili 8.
 *  - Righe generate per articolo: 1. Le categorie con varianti e finiture
 *    generavano una riga per finitura, moltiplicando dataset e occupazione
 *    della tabella `items`.
 *  - Associazioni database: nessun calcolo su `warehouses` di totali mq/ml.
 *  - Complessità del componente: nessun formatter speciale (`decimal2`,
 *    `dimensions`, `scelta`), nessuna logica di unità di prezzo —
 *    `priceUnit()` ricade sul semplice "/pz".
 *  - Tassonomia: nessun elenco di sotto-categorie da mantenere.
 *  - Dipendenze esterne e costo di parsing: nessuna. L'unico importatore
 *    rimasto legge CSV senza librerie Excel/ODS.
 *
 * COMPORTAMENTO
 * -------------
 *  - VUE_APP_DEMO_MODE=true  → la voce Listini è nascosta e le rotte bloccate.
 *  - VUE_APP_DEMO_MODE=false → è accessibile la sola scheda generica, vuota.
 *
 * In entrambi i casi `listiniCategories()` restituisce sempre e soltanto la
 * scheda generica. I componenti generici (ListiniPage, ListiniCategoryPage,
 * CreateItemPage, EditItemPage) restano nel progetto e sono funzionanti.
 */

/** L'unica scheda di listino prevista dal progetto. */
export const GENERIC_CATEGORY = Object.freeze({
  slug: "altro",
  label: "Listino",
  icon: "fa-solid fa-box",
});

/** Slug ammessi come parametro `:category` delle rotte listini. */
export const ALLOWED_CATEGORY_SLUGS = Object.freeze([GENERIC_CATEGORY.slug]);

/** Vero quando la build gira in modalità demo. */
export function isDemoMode() {
  return process.env.VUE_APP_DEMO_MODE === "true";
}

/**
 * Schede di listino esposte dall'interfaccia.
 * Sempre e soltanto la scheda generica.
 *
 * @returns {Array<{slug: string, label: string, icon: string}>}
 */
export function listiniCategories() {
  return [GENERIC_CATEGORY];
}

/**
 * Vero se lo slug corrisponde alla sola scheda esistente.
 *
 * @param {string} slug valore del parametro di rotta `:category`
 */
export function isAllowedCategory(slug) {
  return ALLOWED_CATEGORY_SLUGS.includes(String(slug || "").toLowerCase());
}

/** Rotte che appartengono alla sezione Listini. */
export const LISTINI_ROUTES = Object.freeze([
  "listini",
  "listini-category",
  "listini-add-item",
  "listini-edit-item",
]);

/**
 * Decide se una navigazione verso i Listini è consentita.
 *
 * Logica pura, senza dipendenze dal router: riceve nome di rotta e parametri,
 * restituisce `null` se la navigazione può proseguire, oppure il nome della
 * rotta verso cui reindirizzare.
 *
 *  - In modalità demo l'intera sezione è chiusa → Home.
 *  - Fuori dalla demo esiste una sola scheda (`altro`): un URL che punta a una
 *    vecchia categoria merceologica viene riportato all'indice dei listini,
 *    non alla scheda generica, per non spacciare una categoria per un'altra.
 *
 * @param {{ name?: string, params?: object }} to rotta di destinazione
 * @param {boolean} [demoMode] default: valore corrente della modalità demo
 * @returns {string|null} nome della rotta di redirect, oppure null
 */
export function listiniRedirectFor(to, demoMode = isDemoMode()) {
  const name = to && to.name;
  if (!LISTINI_ROUTES.includes(name)) return null;

  if (demoMode) return "home";

  const category = to.params && to.params.category;
  if (category !== undefined && !isAllowedCategory(category)) {
    return "listini";
  }

  return null;
}

export default listiniCategories;
